import type { OSType } from 'models/metric';

declare const window: any;

export const isMobile = (): boolean => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Check typeof window to protect against SSR case
export const isTouchEnabled = typeof window !== 'undefined' && matchMedia('(hover: none)').matches;

export const LOW_MEMORY_DEVICE_THRESHOLD_GB = 4;

type NetworkInformationSnapshot = {
    effectiveType?: string;
    saveData?: boolean;
};

type NavigatorWithConnection = Navigator & {
    connection?: NetworkInformationSnapshot;
};

const SLOW_EFFECTIVE_CONNECTION_TYPES = new Set(['slow-2g', '2g', '3g']);

/**
 * True when the browser reports <= `LOW_MEMORY_DEVICE_THRESHOLD_GB` GB of RAM via the
 * Device Memory API. SSR-safe: returns false on the server and when the API is
 * unavailable. `navigator.deviceMemory` is a cheap static property, so this reads it
 * live each call rather than caching (keeping it trivially testable and consistent
 * whether called at module init or from a hook).
 *
 * Use this for module-level / non-React consumers (stores, query-client config). React
 * components on server-rendered paths should use `useIsLowMemoryDevice` instead so the
 * first client paint matches the SSR output.
 */
export const isLowMemoryDevice = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    const gb = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    return gb !== undefined && gb <= LOW_MEMORY_DEVICE_THRESHOLD_GB;
};

/** True when the user turned on Save-Data: nothing should download ahead of need. */
export const isSaveDataEnabled = (): boolean =>
    typeof navigator !== 'undefined' && (navigator as NavigatorWithConnection).connection?.saveData === true;

/**
 * True when Chromium reports a 3G-or-slower effective connection, or the user
 * explicitly enabled Save-Data. The Network Information API is not universal,
 * so unsupported browsers keep the normal rendering path.
 */
export const isSlowNetworkConnection = (): boolean => {
    if (typeof navigator === 'undefined') return false;

    const connection = (navigator as NavigatorWithConnection).connection;
    if (!connection) return false;

    return isSaveDataEnabled() || SLOW_EFFECTIVE_CONNECTION_TYPES.has(connection.effectiveType ?? '');
};

/** Shared policy signal for work that can be reduced or deferred without affecting core trading. */
export const isConstrainedDevice = (): boolean => isLowMemoryDevice() || isSlowNetworkConnection();

export function isScrolledIntoView(el: Element): boolean {
    const rect = el.getBoundingClientRect();
    const elemTop = rect.top;
    const elemBottom = rect.bottom;

    // Only completely visible elements return true:
    const isVisible = elemTop >= 0 && elemBottom <= window.innerHeight;
    // Partially visible elements return true:
    //isVisible = elemTop < window.innerHeight && elemBottom >= 0;
    return isVisible;
}

export function isPartlyScrolledIntoView(el: Element): boolean {
    const rect = el.getBoundingClientRect();
    const elemTop = rect.top;
    const elemBottom = rect.bottom;

    // Partially visible elements return true:
    const isVisible = elemTop < window.innerHeight && elemBottom >= 0;
    return isVisible;
}

export const isSafari = (): boolean => {
    // Check if running in SSR environment
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }

    const ua = navigator.userAgent.toLowerCase();
    // Check for Safari browser (but not Chrome, which includes Safari in its UA)
    return ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1;
};

export const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' | undefined => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return undefined;
    }

    if (isMobile()) {
        return 'mobile';
    }

    const userAgent = navigator.userAgent;
    if (/iPad|Android|Tablet/.test(userAgent)) {
        return 'tablet';
    }

    return 'desktop';
};

export const getOperatingSystem = (): OSType | undefined => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return undefined;
    }

    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) {
        return 'Windows Phone';
    }

    if (/android/i.test(userAgent)) {
        return 'Android';
    }

    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
        return 'iOS';
    }

    if (/Win/.test(userAgent)) {
        return 'Windows';
    }

    if (/Mac/.test(userAgent)) {
        return 'MacOS';
    }

    if (/Linux/.test(userAgent)) {
        return 'Linux';
    }

    return 'unknown';
};

export const isIOS = (): boolean => {
    return getOperatingSystem() === 'iOS';
};

export const isAndroid = (): boolean => {
    return getOperatingSystem() === 'Android';
};

export const isDesktop = (): boolean => {
    return getDeviceType() === 'desktop';
};

// Apple Pay on the Web runs outside Safari via Apple's QR-code handoff (the user
// scans with their iPhone, iOS 18+), so eligibility is iOS/Safari by default plus
// desktop browsers once the non-Safari rollout gate is on. SSR-safe: false on the
// server.
export const isApplePayEligibleBrowser = (allowNonSafariDesktop: boolean): boolean => {
    return isIOS() || isSafari() || (allowNonSafariDesktop && isDesktop());
};

export const isWebView = (): boolean => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }

    const ua = navigator.userAgent.toLowerCase();
    const standalone = (navigator as any).standalone;
    const ios = /ip(ad|hone|od)/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const android = /android/.test(ua);
    const safari = /safari/.test(ua);

    return (android && /; wv\)/.test(ua)) || (ios && !standalone && !safari);
};

/**
 * True when the page is hosted in a system WebView (Kalshi AuthenticatedWebView,
 * Android `wv`, Instagram/etc. WKWebViews without a Safari token).
 *
 * Intentionally equals `isWebView()` — do **not** infer SFSafariViewController via
 * missing `safari.pushNotification`. That API is macOS-oriented; real iPhone Safari
 * also lacks it, which would mis-classify full Safari friend shares as in-app.
 * Messages SFSafariViewController keeps Safari's UA and therefore the lifted friend shell.
 */
export const isInAppBrowser = (): boolean => isWebView();
